/**
 * Diamond Search Hook
 *
 * Provides smart diamond search capabilities for the chat assistant.
 * Parses natural language queries and converts them to filter parameters.
 * Uses events to communicate with the Atelier page.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Custom event for diamond search
export const DIAMOND_SEARCH_EVENT = 'diamond-search-apply-filters';
// Shape aliases for natural language parsing
const SHAPE_ALIASES = {
    'round': 'Round',
    'round brilliant': 'Round',
    'brilliant': 'Round',
    'princess': 'Princess',
    'square': 'Princess',
    'cushion': 'Cushion',
    'cushion cut': 'Cushion',
    'oval': 'Oval',
    'emerald': 'Emerald',
    'emerald cut': 'Emerald',
    'radiant': 'Radiant',
    'asscher': 'Asscher',
    'marquise': 'Marquise',
    'pear': 'Pear',
    'teardrop': 'Pear',
    'heart': 'Heart',
    'heart shaped': 'Heart',
};
// Color grades
const COLOR_GRADES = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];
// Clarity grades (ordered from highest to lowest for matching)
const CLARITY_GRADES = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'];
// Clarity aliases for partial matches (e.g., "VS" means VS1 or VS2)
const CLARITY_ALIASES = {
    'flawless': 'FL',
    'internally flawless': 'IF',
    'vvs': 'VVS1', // Default to VVS1 for "VVS"
    'vs': 'VS1', // Default to VS1 for "VS"
    'si': 'SI1', // Default to SI1 for "SI"
    'included': 'I1',
};
// Cut quality terms
const CUT_ALIASES = {
    'excellent': 'Excellent',
    'ideal': 'Excellent',
    'ex': 'Excellent',
    'very good': 'Very Good',
    'vg': 'Very Good',
    'good': 'Good',
    'fair': 'Fair',
    'poor': 'Poor',
};
// Lab aliases
const LAB_ALIASES = {
    'gia': 'GIA',
    'agi': 'AGI',
    'igs': 'IGS',
    'hrd': 'HRD',
    'egl': 'EGL',
    'igi': 'IGI',
};
/**
 * Parse natural language search query into diamond filters
 */
export function parseSearchQuery(query) {
    console.log('🔍 [DiamondSearch] Parsing query:', query);
    const filters = {};
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let confidence = 0;
    let matchCount = 0;
    // Parse shape
    for (const [alias, shape] of Object.entries(SHAPE_ALIASES)) {
        if (lowerQuery.includes(alias)) {
            filters.shape = shape;
            parts.push(`${shape} shape`);
            matchCount++;
            console.log(`  ✓ Shape detected: "${alias}" → ${shape}`);
            break;
        }
    }
    // Parse color grade (look for patterns like "D color", "color D", "D-F color")
    // Must be followed by "color/grade/colour" OR be a standalone letter with word boundary
    // Avoid matching common words like "me", "in", "if", "he", "no", etc.
    const colorPatterns = [
        /\b([d-n])\s*(?:color|grade|colour)/i, // "D color", "F colour"
        /(?:color|grade|colour)\s*([d-n])\b/i, // "color D", "grade F"
        /\b([d-n])\s*-\s*([d-n])\s*(?:color|grade)?/i, // "D-F", "D-F color"
    ];
    let colorFound = false;
    for (const pattern of colorPatterns) {
        const colorMatch = lowerQuery.match(pattern);
        if (colorMatch) {
            const color = colorMatch[1].toUpperCase();
            if (COLOR_GRADES.includes(color)) {
                filters.color = color;
                parts.push(`${color} color`);
                matchCount++;
                console.log(`  ✓ Color detected: "${colorMatch[0]}" → ${color}`);
                colorFound = true;
                break;
            }
        }
    }
    // Parse clarity - check exact grades first, then aliases
    let clarityFound = false;
    // First try exact clarity grades (VVS1, VS2, etc.)
    for (const clarity of CLARITY_GRADES) {
        const clarityRegex = new RegExp(`\\b${clarity}\\b`, 'i');
        if (clarityRegex.test(lowerQuery)) {
            filters.clarity = clarity;
            parts.push(`${clarity} clarity`);
            matchCount++;
            clarityFound = true;
            console.log(`  ✓ Clarity detected (exact): "${clarity}"`);
            break;
        }
    }
    // If no exact match, try aliases (VS, VVS, SI, etc.)
    if (!clarityFound) {
        for (const [alias, clarity] of Object.entries(CLARITY_ALIASES)) {
            const aliasRegex = new RegExp(`\\b${alias}\\b`, 'i');
            if (aliasRegex.test(lowerQuery)) {
                filters.clarity = clarity;
                parts.push(`${clarity} clarity`);
                matchCount++;
                console.log(`  ✓ Clarity detected (alias): "${alias}" → ${clarity}`);
                break;
            }
        }
    }
    // Parse cut quality
    for (const [alias, cut] of Object.entries(CUT_ALIASES)) {
        if (lowerQuery.includes(alias)) {
            filters.cut = cut;
            parts.push(`${cut} cut`);
            matchCount++;
            break;
        }
    }
    // Parse lab/certification
    for (const [alias, lab] of Object.entries(LAB_ALIASES)) {
        if (lowerQuery.includes(alias)) {
            filters.lab = lab;
            parts.push(`${lab} certified`);
            matchCount++;
            break;
        }
    }
    // Parse carat weight
    // Patterns: "1 carat", "1.5 ct", "1-2 carats", "under 2 carats", "over 1 carat"
    const caratPatterns = [
        // Range: "1 to 2 carats", "1-2 ct"
        /(\d+(?:\.\d+)?)\s*(?:to|-)\s*(\d+(?:\.\d+)?)\s*(?:carat|ct|carats)/i,
        // Under/below: "under 2 carats"
        /(?:under|below|less than|max)\s*(\d+(?:\.\d+)?)\s*(?:carat|ct|carats)/i,
        // Over/above: "over 1 carat"
        /(?:over|above|more than|at least|min)\s*(\d+(?:\.\d+)?)\s*(?:carat|ct|carats)/i,
        // Single value: "1.5 carat"
        /(\d+(?:\.\d+)?)\s*(?:carat|ct|carats)/i,
    ];
    // Range pattern
    let rangeMatch = lowerQuery.match(caratPatterns[0]);
    if (rangeMatch) {
        filters.minCarat = rangeMatch[1];
        filters.maxCarat = rangeMatch[2];
        parts.push(`${rangeMatch[1]}-${rangeMatch[2]} carats`);
        matchCount++;
    }
    else {
        // Under pattern
        const underMatch = lowerQuery.match(caratPatterns[1]);
        if (underMatch) {
            filters.maxCarat = underMatch[1];
            parts.push(`under ${underMatch[1]} carats`);
            matchCount++;
        }
        else {
            // Over pattern
            const overMatch = lowerQuery.match(caratPatterns[2]);
            if (overMatch) {
                filters.minCarat = overMatch[1];
                parts.push(`over ${overMatch[1]} carats`);
                matchCount++;
            }
            else {
                // Single value (treat as approximate)
                const singleMatch = lowerQuery.match(caratPatterns[3]);
                if (singleMatch) {
                    const carat = parseFloat(singleMatch[1]);
                    filters.minCarat = (carat * 0.9).toFixed(2);
                    filters.maxCarat = (carat * 1.1).toFixed(2);
                    parts.push(`~${singleMatch[1]} carats`);
                    matchCount++;
                }
            }
        }
    }
    // Parse price
    // Patterns: "$5000", "5k", "$5,000", "under $10000", "5000 to 10000"
    const parsePrice = (val) => {
        // Remove $ and commas, handle 'k' suffix
        let cleanVal = val.replace(/[$,]/g, '').trim();
        const hasK = cleanVal.toLowerCase().endsWith('k');
        if (hasK) {
            cleanVal = cleanVal.slice(0, -1);
        }
        const num = parseFloat(cleanVal);
        return hasK ? (num * 1000).toString() : num.toString();
    };
    // Under price: "under $5000", "below $5k", "less than 5000", "budget $3000"
    const underPriceMatch = lowerQuery.match(/(?:under|below|less than|max|budget)\s*\$?\s*(\d+(?:,\d{3})*k?)/i);
    if (underPriceMatch) {
        filters.maxPrice = parsePrice(underPriceMatch[1]);
        parts.push(`under $${Number(filters.maxPrice).toLocaleString()}`);
        matchCount++;
        console.log(`  ✓ Max price detected: "${underPriceMatch[0]}" → $${filters.maxPrice}`);
    }
    else {
        // Over price: "over $5000", "above $5k", "more than 5000"
        const overPriceMatch = lowerQuery.match(/(?:over|above|more than|at least|min|from)\s*\$?\s*(\d+(?:,\d{3})*k?)/i);
        if (overPriceMatch) {
            filters.minPrice = parsePrice(overPriceMatch[1]);
            parts.push(`over $${Number(filters.minPrice).toLocaleString()}`);
            matchCount++;
        }
        else {
            // Price range: "$5000 to $10000", "$5k-$10k", "5000-10000"
            const priceRangeMatch = lowerQuery.match(/\$?\s*(\d+(?:,\d{3})*k?)\s*(?:to|-)\s*\$?\s*(\d+(?:,\d{3})*k?)/i);
            if (priceRangeMatch) {
                filters.minPrice = parsePrice(priceRangeMatch[1]);
                filters.maxPrice = parsePrice(priceRangeMatch[2]);
                parts.push(`$${Number(filters.minPrice).toLocaleString()}-$${Number(filters.maxPrice).toLocaleString()}`);
                matchCount++;
            }
        }
    }
    // Calculate confidence based on matches
    confidence = Math.min(matchCount / 3, 1); // 3+ matches = high confidence
    // Build description
    const description = parts.length > 0
        ? `Searching for: ${parts.join(', ')}`
        : 'No specific filters detected';
    console.log('🔍 [DiamondSearch] Parse result:', {
        filters,
        description,
        confidence: `${(confidence * 100).toFixed(0)}%`,
        matchCount
    });
    return {
        filters,
        description,
        confidence,
    };
}
/**
 * Format filters as a human-readable string
 */
export function formatFiltersDescription(filters) {
    const parts = [];
    if (filters.shape)
        parts.push(`${filters.shape} shape`);
    if (filters.color)
        parts.push(`${filters.color} color`);
    if (filters.clarity)
        parts.push(`${filters.clarity} clarity`);
    if (filters.cut)
        parts.push(`${filters.cut} cut`);
    if (filters.lab)
        parts.push(`${filters.lab} certified`);
    if (filters.minCarat && filters.maxCarat) {
        parts.push(`${filters.minCarat}-${filters.maxCarat} carats`);
    }
    else if (filters.minCarat) {
        parts.push(`${filters.minCarat}+ carats`);
    }
    else if (filters.maxCarat) {
        parts.push(`up to ${filters.maxCarat} carats`);
    }
    if (filters.minPrice && filters.maxPrice) {
        parts.push(`$${Number(filters.minPrice).toLocaleString()}-$${Number(filters.maxPrice).toLocaleString()}`);
    }
    else if (filters.minPrice) {
        parts.push(`$${Number(filters.minPrice).toLocaleString()}+`);
    }
    else if (filters.maxPrice) {
        parts.push(`up to $${Number(filters.maxPrice).toLocaleString()}`);
    }
    return parts.join(', ') || 'All diamonds';
}
/**
 * Dispatch event to apply filters on Atelier page
 */
export function applyDiamondFilters(filters) {
    const event = new CustomEvent(DIAMOND_SEARCH_EVENT, {
        detail: filters,
    });
    window.dispatchEvent(event);
}
/**
 * Hook to use diamond search functionality
 */
export function useDiamondSearch() {
    const navigate = useNavigate();
    const location = useLocation();
    const [lastSearch, setLastSearch] = useState(null);
    /**
     * Search for diamonds with natural language query
     */
    const searchDiamonds = useCallback((query) => {
        const parsed = parseSearchQuery(query);
        setLastSearch(parsed);
        // Navigate to Atelier if not already there
        if (!location.pathname.includes('/atelier') && !location.pathname.includes('/dealer')) {
            navigate('/atelier');
        }
        // Apply filters after a short delay to ensure page is loaded
        setTimeout(() => {
            applyDiamondFilters(parsed.filters);
        }, 100);
        return parsed;
    }, [navigate, location.pathname]);
    return {
        searchDiamonds,
        parseSearchQuery,
        formatFiltersDescription,
        lastSearch,
    };
}
/**
 * Hook to listen for diamond search events (use in Atelier page)
 */
export function useDiamondSearchListener(onFiltersApplied) {
    useEffect(() => {
        const handleSearch = (event) => {
            onFiltersApplied(event.detail);
        };
        window.addEventListener(DIAMOND_SEARCH_EVENT, handleSearch);
        return () => {
            window.removeEventListener(DIAMOND_SEARCH_EVENT, handleSearch);
        };
    }, [onFiltersApplied]);
}
export default useDiamondSearch;
